import { PropertyPath } from 'lodash-es';

import { getConfigByKey as _getConfigByKey } from './getConfigByKey';
import { loadConfig } from './loadConfig';
import { Configuration, InstanceData, ReturnableValue } from './types';

type CacheEntry = { configuration?: Configuration; customPath?: string };

// Generated before next start, so each lob/region pair is parsed once and shared, deep-frozen so a
// write throws instead of leaking into other requests; failed loads are evicted so unknown regions
// cannot grow the map; development skips the cache so build-config reruns show without a restart.
const configurationCache = new Map<string, Promise<CacheEntry>>();

const isDevelopment = () => process.env.NODE_ENV === 'development';

const cacheKeyFor = (lob: string, region: string) => `${lob.toLowerCase()}:${region.toLowerCase()}`;

const evictIfCurrent = (key: string, promise: Promise<CacheEntry>) => {
    if (configurationCache.get(key) === promise) {
        configurationCache.delete(key);
    }
};

type LoadParams = { region: string; lob: string; configFileOverride?: string };

const deepFreeze = <T>(value: T): T => {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
        Object.freeze(value);
        Object.values(value).forEach(deepFreeze);
    }
    return value;
};

const load = async ({ region, lob, configFileOverride }: LoadParams): Promise<CacheEntry> => {
    // Fresh per load: resolvePath writes customPath into it, and an override's path must never
    // become the base path of a later load.
    const instanceData: InstanceData = {};
    const configuration = await loadConfig({ instanceData })({ region, lob, configFileOverride });
    return { configuration: deepFreeze(configuration), customPath: instanceData.customPath };
};

const loadConfiguration = (params: LoadParams): Promise<CacheEntry> => {
    if (params.configFileOverride || isDevelopment()) {
        return load(params);
    }

    const key = cacheKeyFor(params.lob, params.region);
    const cached = configurationCache.get(key);
    if (cached) {
        return cached;
    }

    const pending: Promise<CacheEntry> = load(params).then(
        (entry) => {
            if (entry.configuration === undefined) {
                evictIfCurrent(key, pending);
            }
            return entry;
        },
        (error) => {
            evictIfCurrent(key, pending);
            throw error;
        },
    );
    configurationCache.set(key, pending);
    return pending;
};

const reset = () => {
    configurationCache.clear();
};

const getConfigByKey = async <T extends ReturnableValue>({
    key,
    region,
    lob,
    fallback,
}: {
    key: PropertyPath;
    region: string;
    lob: string;
    fallback?: T;
}): Promise<T | undefined> => {
    if (!region || !lob) {
        throw new Error('configurationConsumer.getConfigByKey requires both region and lob.');
    }

    const { configuration } = await loadConfiguration({ region, lob });

    return _getConfigByKey(configuration)(key, fallback);
};

/**
 * `configuration` is deep-frozen and, outside development, shared by every caller: clone at your
 * boundary before writing.
 */
const getInstance = async ({
    region,
    lob,
    configFileOverride,
    mock,
}: {
    region: string;
    lob: string;
    configFileOverride?: string;
    mock?: InstanceData;
}): Promise<InstanceData> => {
    const key = cacheKeyFor(lob, region);
    // A mock seeds the cache, so it only applies while the cache is active (not under the dev bypass).
    if (mock?.configuration !== undefined && !configurationCache.has(key)) {
        configurationCache.set(
            key,
            Promise.resolve({ configuration: mock.configuration, customPath: mock.customPath }),
        );
    }

    const { configuration, customPath } = await loadConfiguration({
        region,
        lob,
        configFileOverride,
    });

    return { configuration, customPath, region, lob };
};

const JsoncConfigurationConsumer = {
    getInstance,
    reset,
    getConfigByKey,
};

Object.freeze(JsoncConfigurationConsumer);

export default JsoncConfigurationConsumer;
